import * as THREE from 'three'
import Project from './Project'
import gsap from 'gsap'

export default class ProjectsSection
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.resources = _options.resources
        this.camera = _options.camera
        this.passes = _options.passes
        this.objects = _options.objects
        this.areas = _options.areas
        this.zones = _options.zones
        this.tiles = _options.tiles
        this.debug = _options.debug
        this.x = _options.x
        this.y = _options.y

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('projects')
            this.debugFolder.open()
        }

        // Set up
        this.items = []

        this.interDistance = 24
        this.positionRandomess = 5
        this.projectHalfWidth = 9

        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false
        this.container.updateMatrix()

        this.setGeometries()
        this.setMeshes()
        this.setList()
        this.setZone()

        // Add all project from the list
        for(const _options of this.list)
        {
            this.add(_options)
        }
    }

    // Create canvas-based floor texture with project details
    createFloorTexture(title, description, role, company, value)
    {
        const canvas = document.createElement('canvas')
        canvas.width = 1024
        canvas.height = 512
        const ctx = canvas.getContext('2d')

        // Clear with transparent background
        ctx.clearRect(0, 0, 1024, 512)

        // Title
        ctx.fillStyle = '#ffffff'
        ctx.font = 'bold 48px Inter, Arial, sans-serif'
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        ctx.fillText(title.toUpperCase(), 40, 30)

        // Description - word wrap
        ctx.font = '24px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
        const words = description.split(' ')
        let line = ''
        let y = 100
        const maxWidth = 580
        for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' '
            const metrics = ctx.measureText(testLine)
            if (metrics.width > maxWidth && i > 0) {
                ctx.fillText(line, 40, y)
                line = words[i] + ' '
                y += 32
                if (y > 280) break
            } else {
                line = testLine
            }
        }
        ctx.fillText(line, 40, y)

        // Role section
        ctx.font = 'bold 18px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.fillText('ROLE', 660, 100)

        ctx.font = 'bold 22px Inter, Arial, sans-serif'
        ctx.fillStyle = '#ffffff'
        ctx.fillText(role, 660, 125)

        // Company
        ctx.font = 'bold 18px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.fillText('AT', 660, 170)

        ctx.font = 'bold 22px Inter, Arial, sans-serif'
        ctx.fillStyle = '#ffffff'
        ctx.fillText(company, 660, 195)

        // Value/Impact
        ctx.font = 'bold 18px Inter, Arial, sans-serif'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.fillText('VALUE', 660, 260)

        ctx.font = '20px Inter, Arial, sans-serif'
        ctx.fillStyle = '#00ff88'
        // Word wrap for value
        const valueWords = value.split(' ')
        let valueLine = ''
        let valueY = 285
        for (let i = 0; i < valueWords.length; i++) {
            const testLine = valueLine + valueWords[i] + ' '
            const metrics = ctx.measureText(testLine)
            if (metrics.width > 320 && i > 0) {
                ctx.fillText(valueLine, 660, valueY)
                valueLine = valueWords[i] + ' '
                valueY += 26
                if (valueY > 380) break
            } else {
                valueLine = testLine
            }
        }
        ctx.fillText(valueLine, 660, valueY)

        const texture = new THREE.CanvasTexture(canvas)
        texture.needsUpdate = true
        texture.magFilter = THREE.NearestFilter
        texture.minFilter = THREE.LinearFilter
        return texture
    }

    setGeometries()
    {
        this.geometries = {}
        this.geometries.floor = new THREE.PlaneGeometry(16, 8)
    }

    setMeshes()
    {
        this.meshes = {}

        this.resources.items.areaOpenTexture.magFilter = THREE.NearestFilter
        this.resources.items.areaOpenTexture.minFilter = THREE.LinearFilter
        this.meshes.boardPlane = this.resources.items.projectsBoardPlane.scene.children[0]
        this.meshes.areaLabel = new THREE.Mesh(new THREE.PlaneGeometry(2, 0.5), new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.resources.items.areaOpenTexture }))
        this.meshes.areaLabel.matrixAutoUpdate = false
    }

    setList()
    {
        // 4 key publications with descriptions and DOI links
        this.list = [
            {
                name: 'IEEE TITS Publication',
                imageSources: ['./models/projects/ieeeResearch/slideA.png'],
                floorTexture: this.createFloorTexture(
                    'IEEE TITS PUBLICATION',
                    'Automated and Explainable AI to Enhance Prediction of Pedestrian Injury Severity. Integrates AutoML with SHAP pipelines to classify crash severity, providing dual-level explanation to decode interactions between environmental factors and vehicle specs.',
                    'FIRST AUTHOR',
                    'IEEE TITS (Q1, IF:8.5)',
                    '95% accuracy, policy adopted by agencies'
                ),
                link: {
                    href: 'https://ieeexplore.ieee.org/document/10847308',
                    x: -4.8, y: -3,
                    halfExtents: { x: 3.2, y: 1.5 }
                },
                distinctions: []
            },
            {
                name: 'Maritime XAI Anomaly',
                imageSources: ['./models/projects/agenticAI/slideA.png'],
                floorTexture: this.createFloorTexture(
                    'MARITIME XAI ANOMALY',
                    'Explainable Anomaly Detection Framework for Maritime Main Engine Sensor Data. Combines unsupervised anomaly detection with SHAP values, resolving the black-box dilemma for marine engine room condition monitoring.',
                    'FIRST AUTHOR',
                    'SENSORS (MDPI)',
                    'Real-time XAI condition monitoring'
                ),
                link: {
                    href: 'https://doi.org/10.3390/s21155200',
                    x: -4.8, y: -3,
                    halfExtents: { x: 3.2, y: 1.5 }
                },
                distinctions: []
            },
            {
                name: 'Deep Tabular Survey',
                imageSources: ['./models/projects/pmi/slideA.png'],
                floorTexture: this.createFloorTexture(
                    'DEEP TABULAR SURVEY',
                    'A Survey on Deep Tabular Learning. Comprehensive review of DL architectures for tabular data: TabNet, SAINT, FT-Transformer. Evaluates structural evolution from FC networks to attention-based models.',
                    'CO-AUTHOR',
                    'ARXIV PREPRINT',
                    'Comprehensive technical survey'
                ),
                link: {
                    href: 'https://arxiv.org/abs/2410.12034',
                    x: -4.8, y: -3,
                    halfExtents: { x: 3.2, y: 1.5 }
                },
                distinctions: []
            },
            {
                name: 'XAI Seismic Denoising',
                imageSources: ['./models/projects/xaiIndustrial/slideA.png'],
                floorTexture: this.createFloorTexture(
                    'XAI SEISMIC DENOISING',
                    'XAI-driven Contamination for Self-Supervised Denoising with Pixel-Level Anomaly Detection in Seismic Data. Advanced pipeline to enhance structural imaging in noisy seismic profiles using explainable anomaly criteria.',
                    'FIRST AUTHOR',
                    'J. APPLIED GEOPHYSICS',
                    'Preserves true geological structures'
                ),
                link: {
                    href: 'https://doi.org/10.1016/j.jappgeo.2025.105723',
                    x: -4.8, y: -3,
                    halfExtents: { x: 3.2, y: 1.5 }
                },
                distinctions: []
            }
        ]
    }

    setZone()
    {
        const totalWidth = this.list.length * (this.interDistance / 2)

        const zone = this.zones.add({
            position: { x: this.x + totalWidth - this.projectHalfWidth - 6, y: this.y },
            halfExtents: { x: totalWidth, y: 12 },
            data: { cameraAngle: 'projects' }
        })

        zone.on('in', (_data) =>
        {
            this.camera.angle.set(_data.cameraAngle)
            gsap.to(this.passes.horizontalBlurPass.material.uniforms.uStrength.value, { x: 0, duration: 2 })
            gsap.to(this.passes.verticalBlurPass.material.uniforms.uStrength.value, { y: 0, duration: 2 })
        })

        zone.on('out', () =>
        {
            this.camera.angle.set('default')
            gsap.to(this.passes.horizontalBlurPass.material.uniforms.uStrength.value, { x: this.passes.horizontalBlurPass.strength, duration: 2 })
            gsap.to(this.passes.verticalBlurPass.material.uniforms.uStrength.value, { y: this.passes.verticalBlurPass.strength, duration: 2 })
        })
    }

    add(_options)
    {
        const x = this.x + this.items.length * this.interDistance
        let y = this.y
        if(this.items.length > 0)
        {
            y += (Math.random() - 0.5) * this.positionRandomess
        }

        // Create project
        const project = new Project({
            time: this.time,
            resources: this.resources,
            objects: this.objects,
            areas: this.areas,
            geometries: this.geometries,
            meshes: this.meshes,
            debug: this.debugFolder,
            x: x,
            y: y,
            ..._options
        })

        this.container.add(project.container)

        // Add tiles
        if(this.items.length >= 1)
        {
            const previousProject = this.items[this.items.length - 1]
            const start = new THREE.Vector2(previousProject.x + this.projectHalfWidth, previousProject.y)
            const end = new THREE.Vector2(project.x - this.projectHalfWidth, project.y)
            const delta = end.clone().sub(start)
            this.tiles.add({
                start: start,
                delta: delta
            })
        }

        // Save
        this.items.push(project)
    }
}
